import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useLogger} from '#imports';
import {readonlyState} from '@malt/utils';
import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
import {buildAllSteps} from '~/features/freelancer-signup/navigation/store/freelancerSignupNavigationStepFactory';
import {
    type FreelancerSignupGroupStep,
    type FreelancerSignupStepConfiguration,
    FreelancerSignupStepGroup,
    FreelancerSignupStepName,
    FreelancerSignupStepRoute,
} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
import {type Workflow} from '~/features/freelancer-signup/workflow/types/Workflow';
import {useFeatureContextStore} from '~/store/featureStore';

export const useFreelancerSignupNavigationStore = defineStore('freelancer-signup_navigation-store', () => {
    const freelancerSignupStore = useFreelancerSignupStore();
    const {workflow} = storeToRefs(freelancerSignupStore);
    const logger = useLogger();
    const {features} = useFeatureContextStore();

    const context = ref<FreelancerSignupContext>(FreelancerSignupContext.REGULAR);
    const currentStep = ref<FreelancerSignupStepConfiguration>({
        name: FreelancerSignupStepName.FREELANCING_EXPERIENCE,
        routeTo: FreelancerSignupStepRoute.FREELANCING_EXPERIENCE,
        group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
        subStep: 1,
        isAccessible: true,
        isCompleted: false,
        previousEnabled: false,
        hideShapes: true,
    });

    const steps = computed(() => buildAllSteps(context.value, workflow.value as Workflow | null, features));
    const allRoutes = computed(() => steps.value.map((config) => config.routeTo));
    const isLastStep = computed(() => steps.value.length === steps.value.findIndex((step) => step.name === currentStep.value.name) + 1);
    const stepGroups = computed(() => _computeStepGroups(steps.value));

    function getCurrentWorkflowEmail(): string | undefined {
        return freelancerSignupStore.getMaybeWorkflowEmail();
    }

    function setCurrentStepAsFirstStepToComplete(signupContext: FreelancerSignupContext) {
        if (workflow.value) {
            context.value = workflow.value.context;
            const accessibleSteps = steps.value.filter(({isAccessible}) => isAccessible);

            if (accessibleSteps.length === 0) {
                throw new Error('No accessible steps found');
            }

            const firstStepToComplete = accessibleSteps.find(({isCompleted}) => !isCompleted) ?? accessibleSteps[accessibleSteps.length - 1];

            if (firstStepToComplete) {
                currentStep.value = firstStepToComplete;
            } else if (import.meta.dev) {
                logger.warn('setCurrentStepAsFirstStepToComplete - no step to complete with workflow:', workflow.value);
            }
        } else {
            context.value = signupContext;
            currentStep.value = steps.value[0];
        }
    }

    function getMaybeNextStepConfiguration(): FreelancerSignupStepConfiguration | null {
        if (isLastStep.value) {
            logger.error(`Try to go next on the last step => ${currentStep.value.name}, context => ${context.value}`);
            return null;
        }
        const currentStepPosition = steps.value.findIndex((step) => step.name === currentStep.value.name);
        if (currentStepPosition < 0) {
            logger.error(`Current step not found on all steps available: current step => ${currentStep.value.name}, context => ${context.value}`);
            return null;
        }
        const nextStepConfig = steps.value[currentStepPosition + 1];
        if (!nextStepConfig.isAccessible) {
            logger.error(
                `Next step not reachable: current step => ${currentStep.value.name}, next step expected => ${nextStepConfig.name}, context => ${context.value}`,
            );
            return null;
        }

        return nextStepConfig;
    }

    function getMaybePreviousStepConfiguration(): FreelancerSignupStepConfiguration | null {
        if (!currentStep.value.previousEnabled) {
            return null;
        }
        const currentStepPosition = steps.value.findIndex((step) => step.name === currentStep.value.name);
        if (currentStepPosition < 0) {
            logger.error(`Current step not found on all steps available: current step => ${currentStep.value.name}, context => ${context.value}`);
            return null;
        }
        return steps.value[currentStepPosition - 1];
    }

    function updateCurrentContext(providedContext: FreelancerSignupContext): void {
        context.value = providedContext;
    }

    function updateCurrentStep(stepName: FreelancerSignupStepName): void {
        const step = steps.value.find((step) => step.name === stepName);
        if (step && step.name !== currentStep.value.name) {
            currentStep.value = step;
        }
    }

    return {
        // RO A-Z
        allRoutes: readonlyState(allRoutes),
        currentStep: readonlyState(currentStep),
        funnelContext: readonlyState(context),
        isLastStep: readonlyState(isLastStep),
        stepGroups: readonlyState(stepGroups),

        // functions A-Z
        getCurrentWorkflowEmail,
        setCurrentStepAsFirstStepToComplete,
        getMaybeNextStepConfiguration,
        getMaybePreviousStepConfiguration,
        updateCurrentContext,
        updateCurrentStep,
    };

    function _computeStepGroups(steps: FreelancerSignupStepConfiguration[]): FreelancerSignupGroupStep[] {
        const stepper = new Set<FreelancerSignupStepGroup>(steps.map((step) => step.group));
        return [...stepper].map((group, index) => {
            return {
                group,
                groupPosition: index + 1,
                nbSubStep: steps.filter((step) => step.group === group).length,
            };
        });
    }
});
